import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Footer } from '../components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { LockKeyhole, Mail, Shield } from 'lucide-react';
import { PasswordStrength, getPasswordStrength } from '@/components/PasswordStrength';
import { toast } from 'sonner';
import { getAccessOptions, getRememberedAccess } from '@/lib/accessOptions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Login() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [recoveryOpen, setRecoveryOpen] = useState(false);
  const [recoveryStep, setRecoveryStep] = useState('email');
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [recoveryError, setRecoveryError] = useState('');
  const settings = { app_name: 'CCBRJM', subtitle: 'Gestão de Pessoas' };

  useEffect(() => {
    const urlError = searchParams.get('error');
    if (urlError) toast.error(decodeURIComponent(urlError));
  }, [searchParams]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Falha na autenticação');
      if (data.must_change_password) {
        window.location.href = '/alterar-senha';
        return;
      }
      const options = getAccessOptions(data);
      const remembered = getRememberedAccess(options);
      window.location.href = remembered?.route || (options.length > 1 ? '/acessos' : options[0]?.route || '/admin');
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const requestRecoveryCode = async () => {
    setRecoveryError('');
    setRecoveryLoading(true);
    try {
      const res = await fetch(`${API}/auth/forgot-password`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: recoveryEmail || email }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Não foi possível enviar o código.');
      setRecoveryEmail(recoveryEmail || email);
      setRecoveryStep('code');
      toast.success('Código enviado por SMS');
    } catch (err) { setRecoveryError(err.message); } finally { setRecoveryLoading(false); }
  };

  const resetPassword = async () => {
    setRecoveryError('');
    if (newPassword !== confirmPassword) { setRecoveryError('A confirmação não confere com a nova senha.'); return; }
    if (!getPasswordStrength(newPassword).valid) { setRecoveryError('A nova senha precisa cumprir todos os requisitos.'); return; }
    setRecoveryLoading(true);
    try {
      const res = await fetch(`${API}/auth/reset-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ email: recoveryEmail, code: recoveryCode, new_password: newPassword }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Não foi possível redefinir a senha.');
      toast.success('Senha redefinida. Faça login com a nova senha.');
      setRecoveryOpen(false);
      setPassword('');
      setEmail(recoveryEmail);
    } catch (err) { setRecoveryError(err.message); } finally { setRecoveryLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,#dbeafe_0,#f8fafc_34%,#eef2ff_100%)] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="w-full p-8 bg-white/90 backdrop-blur-sm shadow-xl border border-white/70" data-testid="login-card">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-gradient-to-br from-blue-600 to-blue-500 rounded-2xl mx-auto mb-6 flex items-center justify-center shadow-lg">
              <Shield className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Work Sans, sans-serif' }} data-testid="login-title">
              {settings.app_name}
            </h1>
            <p className="text-slate-600 text-base" data-testid="login-subtitle">
              {settings.subtitle}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="email-password-login-form">
            {error && <Alert variant="destructive" data-testid="login-error-alert"><AlertDescription>{error}</AlertDescription></Alert>}
            <div className="space-y-2">
              <Label htmlFor="login-email">E-mail</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input id="login-email" data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="pl-9" autoComplete="username" required />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="login-password">Senha</Label>
              <div className="relative">
                <LockKeyhole className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input id="login-password" data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="pl-9" autoComplete="current-password" required />
              </div>
            </div>
            <Button type="submit" data-testid="login-submit-button" disabled={loading} className="w-full rounded-xl bg-blue-600 hover:bg-blue-700">
              {loading ? 'Entrando...' : 'Entrar'}
            </Button>
            <Button type="button" variant="ghost" data-testid="forgot-password-open-button" onClick={() => { setRecoveryOpen(true); setRecoveryEmail(email); setRecoveryStep('email'); }} className="w-full text-sm text-blue-700 hover:text-blue-800">
              Recuperar senha pelo celular
            </Button>
          </form>
        </Card>
        <Dialog open={recoveryOpen} onOpenChange={setRecoveryOpen}>
          <DialogContent className="w-[calc(100vw-2rem)] max-w-md p-5 sm:p-6" data-testid="password-recovery-modal">
            <DialogHeader>
              <DialogTitle>Recuperar senha</DialogTitle>
            </DialogHeader>
            <div className="w-full" data-testid="password-recovery-card">
            <div className="space-y-4">
              <div>
                <p className="text-sm text-slate-500">Enviaremos um código por SMS para o celular cadastrado.</p>
              </div>
              {recoveryError && <Alert variant="destructive" data-testid="password-recovery-error"><AlertDescription>{recoveryError}</AlertDescription></Alert>}
              {recoveryStep === 'email' ? (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="recovery-email">E-mail</Label>
                    <Input id="recovery-email" data-testid="password-recovery-email-input" type="email" value={recoveryEmail} onChange={(e) => setRecoveryEmail(e.target.value)} />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                    <Button type="button" variant="outline" onClick={() => setRecoveryOpen(false)} data-testid="password-recovery-cancel-button" className="w-full min-h-11">Cancelar</Button>
                    <Button type="button" onClick={requestRecoveryCode} disabled={recoveryLoading || !recoveryEmail} data-testid="password-recovery-send-code-button" className="w-full min-h-11 bg-blue-600 hover:bg-blue-700">Enviar código</Button>
                  </div>
                </>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="recovery-code">Código SMS</Label>
                    <Input id="recovery-code" data-testid="password-recovery-code-input" value={recoveryCode} onChange={(e) => setRecoveryCode(e.target.value)} inputMode="numeric" maxLength={6} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="recovery-new-password">Nova senha</Label>
                    <Input id="recovery-new-password" data-testid="password-recovery-new-password-input" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
                    <PasswordStrength password={newPassword} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="recovery-confirm-password">Confirmar senha</Label>
                    <Input id="recovery-confirm-password" data-testid="password-recovery-confirm-password-input" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                    <Button type="button" variant="outline" onClick={() => setRecoveryStep('email')} data-testid="password-recovery-back-button" className="w-full min-h-11">Voltar</Button>
                    <Button type="button" onClick={resetPassword} disabled={recoveryLoading || !recoveryCode || !newPassword} data-testid="password-recovery-reset-button" className="w-full min-h-11 bg-blue-600 hover:bg-blue-700">Redefinir</Button>
                  </div>
                </>
              )}
            </div>
          </div>
          </DialogContent>
        </Dialog>
        <Footer />
      </div>
    </div>
  );
}